<template>
    <DsModal
        :is-open="isOpen"
        size="lg"
        class="export-xml-modal"
        @close="closeExportXml"
    >
        <template #heading>
            <DsModalHeader>
                <template #leading>
                    <DsIconButton name="x" @click="closeExportXml" />
                </template>
                <template #title>
                    <DsModalHeaderTitle>
                        <template #title>
                            {{ $t('funnel.edit.debug.export') }}
                        </template>
                    </DsModalHeaderTitle>
                </template>
                <template #trailing>
                    <DsFilledButton leading-icon="copy" @click="copyCode">
                        {{ $t('funnel.edit.copy.code') }}
                    </DsFilledButton>
                </template>
            </DsModalHeader>
        </template>
        <div class="export-options-wrapper">
            <DsRadioGroup
                v-model="xmlToExport"
                :options="exportOptions"
                class="export-options"
                @input="toggleExportXml"
            />
            <DsMultiselect
                v-model="selectedVersion"
                single
                bind-value-only
                placeholder="Choose save version"
                :options="restoreVersions"
                class="select-version"
                @input="getXmlFromIdVersion"
            />
        </div>
        <codemirror
            v-model="xmlFile"
            class="xml-code"
            :options="codeMirrorOptions"
        />
    </DsModal>
</template>
<script>
import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/xml/xml';

import { getXmlFromIdVersion, mapRevisionsInfoToRevisionsList } from './export.xml.api';
import { loadRestoredVersions } from '../restoreVersion/restore.version.api';
import { CONSTANTS_STEALTH_MENU } from '../../utils/editor.constants';

export default {
    CONSTANTS_STEALTH_MENU,

    components: {
        codemirror,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        funnelId: {
            type: Number,
            default: 0,
        },
        currentFunnelDraftXml: {
            type: String,
            default: '',
        },
        originalFunnelDraftXml: {
            type: String,
            default: '',
        },
        originalFunnelPublishedXml: {
            type: String,
            default: '',
        },
    },
    emits: ['close-export-xml'],
    data() {
        return {
            xmlFile: this.currentFunnelDraftXml,
            xmlToExport: CONSTANTS_STEALTH_MENU.current,
            codeMirrorOptions: {
                tabSize: 4,
                theme: 'default',
                mode: 'text/xml',
                lineNumbers: true,
                line: true,
            },
            selectedVersion: '',
            restoreVersions: [],
            exportOptions: [
                { value: CONSTANTS_STEALTH_MENU.current, label: this.$t('funnel.edit.export.current') },
                { value: CONSTANTS_STEALTH_MENU.original, label: this.$t('funnel.edit.export.original') },
                { value: CONSTANTS_STEALTH_MENU.published, label: this.$t('funnel.edit.export.published') },
            ],
        };
    },

    async mounted() {
        await this.loadRestoredVersions();
        this.resizeComponent();
    },

    beforeUnmount() {
        window.removeEventListener('resize');
    },

    methods: {
        async loadRestoredVersions() {
            try {
                const data = await loadRestoredVersions(this.funnelId);
                const { revisions } = data;

                this.restoreVersions = await mapRevisionsInfoToRevisionsList(revisions);
            } catch (error) {
                this.$error({ message: this.$t('funnel.edit.restore.failure') });
            }
        },
        async getXmlFromIdVersion() {
            try {
                this.xmlToExport = '';

                const data = await getXmlFromIdVersion(this.selectedVersion);

                this.xmlFile = data;
            } catch (error) {
                this.$error({ message: this.$t('funnel.edit.export.error') });
            }
        },

        async toggleExportXml() {
            try {
                if (this.xmlToExport === CONSTANTS_STEALTH_MENU.current) {
                    this.xmlFile = this.currentFunnelDraftXml;
                } else if (this.xmlToExport === CONSTANTS_STEALTH_MENU.original) {
                    this.xmlFile = this.originalFunnelDraftXml;
                } else if (this.xmlToExport === CONSTANTS_STEALTH_MENU.published) {
                    this.xmlFile = this.originalFunnelPublishedXml;
                }
            } catch (error) {
                this.$error({ message: this.$t('funnel.edit.export.error') });
            }
        },

        async copyCode() {
            try {
                await this.$copyText(this.xmlFile);
                this.$toast({ message: this.$t('funnel.edit.copy.success') });
            } catch (err) {
                this.$error({ message: this.$t('funnel.edit.copy.error'), bottom: true });
            }
        },

        resizeComponent() {
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, '1000');
        },

        closeExportXml() {
            this.$emit('close-export-xml');
        },
    },
};
</script>

<style lang="scss">
    .export-xml-modal {
        div.modal-content-wrapper {
            height: 802px;
        }
        div.modal-body {
            padding: 0;
        }

        div.select-version.multiselect {
            margin-bottom: 0;
        }

        div > div.multiselect-tags > span > label{
            padding-top: 0px;
        }
    }

    .export-options-wrapper {
        display: flex;
    }

    .export-options {
        display: flex;
        width: 100%;
    }

    .xml-code {
        width: 750px;
        border: 1px solid $color-gray-200;
        margin-top: $spacing-125;
    }

    div.vue-codemirror.xml-code > div {
        height: 635px;
    }

    div.vue-codemirror.xml-code > div > div.CodeMirror-scroll {
        width: 100%;
    }

    .CodeMirror-lines {
        width: fit-content;
    }

    .CodeMirror-selected {
        width: 100% !important;
    }

</style>
